import React, { useState, useEffect } from 'react';
import { Card, Button, Alert, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { paynimoService } from '../services/paynimoService';

const PaynimoCheckout = ({ registrationData, onPaymentSuccess, onCancel }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [paymentConfig, setPaymentConfig] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const initializePaynimo = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        // Create Paynimo payment session
        const sessionData = {
          amount: registrationData.fee,
          currency: 'INR',
          registrationId: registrationData.id,
          customerInfo: {
            name: registrationData.name,
            email: registrationData.email,
            phone: registrationData.phone || '',
            countryCode: 'IN'
          }
        };

        const sessionResponse = await paynimoService.createSession(sessionData);
        setPaymentConfig(sessionResponse.data);

      } catch (error) {
        console.error('Paynimo initialization failed:', error);
        setError(error.message);
        toast.error(error.message);
      } finally {
        setIsLoading(false);
      }
    };

    initializePaynimo();
  }, [registrationData]);

  const handlePaynimoResponse = async (response) => {
    try {
      console.log('Paynimo response received:', response);
      
      // Check response status
      if (typeof response !== "undefined" && 
          typeof response.paymentMethod !== "undefined" && 
          typeof response.paymentMethod.paymentTransaction !== "undefined" && 
          typeof response.paymentMethod.paymentTransaction.statusCode !== "undefined") {
        
        const statusCode = response.paymentMethod.paymentTransaction.statusCode;
        
        if (statusCode === "0300") {
          // Success
          toast.success('Payment successful!');
          const processedResponse = await paynimoService.handleResponse(response);
          onPaymentSuccess(processedResponse);
        } else if (statusCode === "0398") {
          // Initiated/Pending
          toast.info('Payment initiated. Please wait for confirmation.');
          await paynimoService.handleResponse(response);
          // You might want to poll for status or show a waiting screen
        } else {
          // Error
          const errorMessage = response.paymentMethod.paymentTransaction.errorMessage || 'Payment failed';
          toast.error(errorMessage);
          await paynimoService.handleResponse(response);
        }
      } else {
        // Invalid response format
        toast.error('Invalid payment response received');
      }
    } catch (error) {
      console.error('Error handling Paynimo response:', error);
      toast.error('Failed to process payment response');
    }
  };

  const initiatePayment = () => {
    if (!paymentConfig || !paymentConfig.config) {
      toast.error('Payment configuration not ready');
      return;
    }

    // Wait for libraries to load if needed
    const checkLibrariesAndProceed = () => {
      try {
        // Check if jQuery is available
        if (typeof window.$ === 'undefined') {
          console.log('jQuery not loaded, waiting...');
          setTimeout(checkLibrariesAndProceed, 500);
          return;
        }
        
        // Check if Paynimo checkout is available
        if (typeof window.$.pnCheckout !== 'function') {
          console.log('Paynimo checkout not loaded, waiting...');
          setTimeout(checkLibrariesAndProceed, 500);
          return;
        }

        // Both libraries are loaded, proceed with payment
        console.log('Libraries loaded successfully, initiating payment...');
        
        // Add response handler to config and ensure all required fields are present
        const configWithHandler = {
          ...paymentConfig.config,
          consumerData: {
            ...paymentConfig.config.consumerData,
            responseHandler: handlePaynimoResponse,
            // Ensure these fields are present as they're used in token generation
            consumerMobileNo: paymentConfig.config.consumerData.consumerMobileNo || '',
            consumerEmailId: paymentConfig.config.consumerData.consumerEmailId || ''
          }
        };

        console.log('Paynimo Config:', configWithHandler);

        // Initialize Paynimo checkout
        window.$.pnCheckout(configWithHandler);
        
        // Show loading state
        setIsLoading(true);
        
      } catch (error) {
        console.error('Failed to initiate Paynimo payment:', error);
        toast.error('Failed to initiate payment: ' + error.message);
      }
    };

    // Start checking for libraries
    checkLibrariesAndProceed();
  };

  if (isLoading && !paymentConfig) {
    return (
      <Card className="shadow">
        <Card.Body className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3">Initializing Paynimo payment system...</p>
        </Card.Body>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="shadow">
        <Card.Header className="bg-danger text-white">
          <h4 className="mb-0">Payment Error</h4>
        </Card.Header>
        <Card.Body>
          <Alert variant="danger">
            {error}
          </Alert>
          <div className="d-flex justify-content-between">
            <Button variant="secondary" onClick={onCancel}>
              Go Back
            </Button>
            <Button variant="primary" onClick={() => window.location.reload()}>
              Retry
            </Button>
          </div>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card className="shadow">
      <Card.Header className="bg-primary text-white">
        <h4 className="mb-0">Complete Payment - Paynimo</h4>
      </Card.Header>
      <Card.Body>
        <Alert variant="info" className="mb-4">
          <div className="d-flex justify-content-between">
            <div>
              <strong>Registration Fee: ₹{registrationData.fee}</strong>
              <br />
              <small>Registration ID: {registrationData.id}</small>
            </div>
            <div className="text-end">
              <small>Participant: {registrationData.name}</small>
            </div>
          </div>
        </Alert>

        <Alert variant="success" className="mb-4">
          <h6>✅ Secure Payment with Paynimo</h6>
          <ul className="mb-0">
            <li>Multiple payment options: Cards, Net Banking, UPI, Wallets</li>
            <li>Bank-grade security with SSL encryption</li>
            <li>Instant payment confirmation</li>
            <li>24/7 customer support</li>
          </ul>
        </Alert>

        {paymentConfig && (
          <div className="text-center">
            <p className="mb-3">
              <strong>Transaction ID:</strong> {paymentConfig.txnId}
            </p>
            <p className="mb-4 text-muted">
              Click "Pay Now" to proceed to secure payment gateway
            </p>
          </div>
        )}

        <div className="d-flex justify-content-between">
          <Button
            variant="secondary"
            onClick={onCancel}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            variant="success"
            onClick={initiatePayment}
            disabled={isLoading || !paymentConfig}
            size="lg"
          >
            {isLoading ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                Processing...
              </>
            ) : (
              `Pay ₹${registrationData.fee} Now`
            )}
          </Button>
        </div>

        <Alert variant="warning" className="mt-4">
          <small>
            <strong>Security Notice:</strong> You will be redirected to Paynimo's secure payment gateway. 
            Your payment information is encrypted and secure. We do not store your card details.
          </small>
        </Alert>
      </Card.Body>
    </Card>
  );
};

export default PaynimoCheckout;
